import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  IoEyeOutline,
  IoEyeOffOutline,
  IoLockClosedOutline,
} from 'react-icons/io5';
import { FaArrowRightLong } from 'react-icons/fa6';

import { resetPassword } from '../../services/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Matches Login.jsx's theme scaffolding — hardcoded light for now,
  // ready to wire up to a real theme context/store later.
  const theme = 'light';
  const isDark = theme === 'dark';

  useEffect(() => {
    if (!token) {
      toast.error('Invalid or missing reset token.');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading('Resetting password...');
    try {
      await resetPassword(token, password);
      toast.success('Password successfully reset! You can now log in.', {
        id: toastId,
      });
      navigate('/login');
    } catch (error) {
      toast.error(error.message || 'Failed to reset password', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) return null;

  const inputClassName = `
    w-full rounded-lg border px-3 py-3 pr-10 text-sm focus:outline-none
    ${
      isDark
        ? 'border-transparent bg-[#1E1F20] text-zinc-200 focus:border-[#6a65ef]'
        : 'border-zinc-300 bg-white text-zinc-900 focus:border-[#6a65ef]'
    }
  `;

  return (
    <div
      className={`
      relative flex h-screen w-screen flex-col items-center justify-center overflow-hidden
      ${isDark ? 'bg-black' : 'bg-radial-gradient'}
    `}
    >
      {isDark && (
        <img
          src="/assets/auth-bg.svg"
          className="absolute inset-0 z-0 hidden h-full w-full object-cover lg:block"
          alt="Background"
        />
      )}

      <div className="relative z-10 w-full max-w-md p-5">
        {/* LOGO */}
        <div className="mb-5 flex items-center">
          <img
            alt="SuperManager AGI"
            src="/assets/super_agi_logo.png"
            className="h-16 w-16"
          />
          <h1
            className={`${isDark ? 'text-white' : 'text-black'} font-poppins ml-2 text-lg font-bold`}
          >
            SuperManager AGI
          </h1>
        </div>

        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#6a65ef]/10">
            <IoLockClosedOutline className="h-5 w-5 text-[#6a65ef]" />
          </div>
          <div>
            <h2
              className={`${isDark ? 'text-white' : 'text-black'} text-2xl font-bold`}
            >
              Set New Password
            </h2>
            <p
              className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
            >
              Please enter your new password below.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="relative mb-4">
            <label
              htmlFor="new-password"
              className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
            >
              New Password
            </label>
            <div className="relative mt-1">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={inputClassName}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-black'}`}
              >
                {showPassword ? (
                  <IoEyeOffOutline className="h-5 w-5" />
                ) : (
                  <IoEyeOutline className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="relative mb-6">
            <label
              htmlFor="confirm-password"
              className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}
            >
              Confirm Password
            </label>
            <div className="relative mt-1">
              <input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className={inputClassName}
              />
            </div>
          </div>

          <div className="flex flex-col items-start gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-auto items-center gap-4 rounded-3xl border bg-white p-[1px] disabled:pointer-events-none disabled:opacity-50"
            >
              <div className="font-poppins flex h-9 items-center justify-center px-6 font-medium text-black">
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </div>
              {!isSubmitting && (
                <span className="mr-[1px] flex h-9 w-9 items-center justify-center rounded-full bg-[#6a65ef] text-lg text-white">
                  <FaArrowRightLong />
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
